/**
 * TiendaComplementosToggle — toggle en Admin Panel para mostrar/ocultar
 * la tienda de complementos (/complementos) a los clientes.
 *
 * Por defecto está OCULTA. Al activarse, aparece en el sidebar de propietarios.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ShoppingBag, Eye, EyeOff } from 'lucide-react';
import api from '@/lib/api';

export default function TiendaComplementosToggle() {
  const [visible, setVisible] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await api.get('/admin/config-global');
      setVisible(!!r.data.tienda_complementos_visible);
    } catch (e) {
      toast.error('Error cargando configuración');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const toggle = async (nuevo) => {
    setGuardando(true);
    try {
      await api.put('/admin/config-global', { tienda_complementos_visible: nuevo });
      setVisible(nuevo);
      toast.success(nuevo ? 'Tienda visible para clientes' : 'Tienda oculta para clientes');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <div className="h-24 bg-slate-800/40 rounded animate-pulse" />;
  }

  return (
    <Card className="bg-slate-900/40 border-slate-800" data-testid="tienda-toggle-card">
      <CardContent className="p-4 sm:p-5 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${
          visible ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-800 text-slate-500'
        }`}>
          {visible ? <Eye className="w-6 h-6" /> : <EyeOff className="w-6 h-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingBag className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-bold text-slate-100">Tienda de Complementos</h3>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
              visible ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                      : 'bg-slate-800 text-slate-400 border border-slate-700'
            }`}>
              {visible ? 'Visible' : 'Oculta'}
            </span>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            Cuando está activa, los clientes ven un menú &quot;Complementos&quot; con add-ons que pueden probar 7 días gratis.
            {' '}Manténla oculta si aún no vas a comercializarla — puedes activarla en cualquier momento.
          </p>
        </div>
        <Switch checked={visible} onCheckedChange={toggle} disabled={guardando}
          className="data-[state=checked]:bg-emerald-500"
          data-testid="tienda-toggle-switch" />
      </CardContent>
    </Card>
  );
}
